import { css, cx } from '@linaria/core'
import { type FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '#components/Button'
import { Checkbox } from '#components/Checkbox'
import { Modal } from '#components/Modal'
import { exportFavoriteSetsAsJson } from '#features/FavoriteSets/exportFavoriteSetsAsJson'
import type { FavoriteSet, FavoriteSetId } from '#features/FavoriteSets/FavoriteSet'
import { sharedTestIds } from '#utils/sharedTestIds'

const lightboxClass = css`
  padding-top: 48px;
`

export const lightboxContentClass = css`
  display: flex;
  flex-direction: column;
  max-height: 80vh;
  min-height: 200px;
  min-width: 300px;
  max-width: 500px;
  padding: 24px;
  gap: 24px;

  > main {
    flex: 1;
    overflow-y: auto;

    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-left: 1px;
  }

  > footer {
    gap: 20px;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: flex-end;
  }
`

export const ExportFavoritesModal: FC<{
  className?: string
  activeFavoriteSet?: FavoriteSet
  favoriteSets: FavoriteSet[]
  initialSelection?: FavoriteSetId[]
  open?: boolean
  onOpenChange: (open: boolean) => void
}> = ({ initialSelection, className, open, onOpenChange, activeFavoriteSet, favoriteSets }) => {
  const { t } = useTranslation()

  const defaultSelection = new Set(initialSelection ?? (activeFavoriteSet ? [activeFavoriteSet.id] : []))
  const [selectedSets, setSelectedSets] = useState(defaultSelection)

  return (
    <Modal className={lightboxClass} contentClassName={cx(className, lightboxContentClass)} open={open} lifted onOpenChange={onOpenChange}>
      <header>
        <h2>Export Favorite Sets</h2>
      </header>
      <main>
        {favoriteSets.map((set, index) => (
          <div key={set.id}>
            <Checkbox
              label={set.name || t('shared:favorite-sets.item.placeholder', 'Favorite Set #{{index}}', { index: index + 1 })}
              checked={selectedSets.has(set.id)}
              onChange={(e) => {
                const newSelection = new Set(selectedSets)
                if (e.currentTarget.checked) {
                  newSelection.add(set.id)
                } else {
                  newSelection.delete(set.id)
                }
                setSelectedSets(newSelection)
              }}
            />
          </div>
        ))}
      </main>
      <footer>
        <Button kind="secondary" onClick={() => onOpenChange(false)}>
          {t('shared:common.button.cancel')}
        </Button>
        <Button
          disabled={selectedSets.size === 0}
          onClick={() => {
            exportFavoriteSetsAsJson(favoriteSets.filter((set) => selectedSets.has(set.id)))
          }}
          data-testid={sharedTestIds.favorites.exportButton}
        >
          {t('shared:favorite-sets.export.export-action', 'Export')}
        </Button>
      </footer>
    </Modal>
  )
}
